/**
 * LangChain Service - Advanced Document Parsing and Job Scraping
 * 
 * This service provides:
 * 1. Universal resume parsing (PDF, DOCX, images, HTML, etc.)
 * 2. Unified job URL scraping with AI-powered extraction
 * 3. Database persistence with fallback support
 * 4. Enhanced error handling and retry mechanisms
 */

import { CheerioWebBaseLoader } from '@langchain/community/document_loaders/web/cheerio';
import { PromptTemplate } from '@langchain/core/prompts';
import { ChatGroq } from '@langchain/groq';
import { RecursiveCharacterTextSplitter } from '@langchain/textsplitters';
import * as cheerio from 'cheerio';
import { LLMChain } from 'langchain/chains';
import { TextLoader } from 'langchain/document_loaders/fs/text';
import { createWorker } from 'tesseract.js';

import { supabase } from '../lib/supabase';
import type { StructuredResume } from '../types/resume';
import { appLogger } from '../utils/appLogger';

// ============================================================================
// CONFIGURATION & TYPES
// ============================================================================

interface LangChainConfig {
  groqApiKey: string;
  temperature: number;
  maxTokens: number;
  model: string;
}

/** Section from document-native parsing (DOCX headings, PDF font/structure). */
export interface ParsedResumeSection {
  title: string;
  content: string;
  preview: string;
}

interface ParsedResumeData {
  structured: StructuredResume;
  rawText: string;
  metadata: {
    format: string;
    pageCount?: number;
    confidence?: number;
    processingTime: number;
  };
  /** Sections from document structure (Word headings / PDF font-size) when available. */
  sections?: ParsedResumeSection[];
}

interface ParsedJobData {
  jobId: string;
  title: string;
  company: string;
  location: string;
  description: string;
  requirements: string[];
  skills: string[];
  salary?: string;
  jobType?: string;
  experienceLevel?: string;
  benefits?: string[];
  metadata: {
    portal: string;
    scrapedAt: Date;
    confidence: number;
    processingTime: number;
  };
}

// ============================================================================
// LANGCHAIN CONFIGURATION — MULTI-PROVIDER FALLBACK CHAIN
// ============================================================================

interface LLMProvider {
  id: string;
  label: string;
  instance: ChatGroq;       // ChatGroq works for any OpenAI-compatible endpoint
  rateLimitedUntil: number;  // epoch ms – skip until this time
}

const extractRetryAfterSec = (err: unknown): number | undefined => {
  const msg = (err as any)?.message || '';
  const m = msg.match(/try again in (\d+)m([\d.]+)s/i);
  if (m) return parseInt(m[1], 10) * 60 + parseFloat(m[2]);
  return undefined;
};

const is429 = (err: unknown): boolean =>
  (err as any)?.status === 429 || String((err as any)?.message ?? '').includes('429');

let lastApiCall = 0;
const MIN_CALL_INTERVAL = 80;
const waitForRateLimit = async () => {
  const now = Date.now();
  const gap = now - lastApiCall;
  if (gap < MIN_CALL_INTERVAL) {
    await new Promise(r => setTimeout(r, MIN_CALL_INTERVAL - gap));
  }
  lastApiCall = Date.now();
};

/**
 * Build the ordered provider list.
 * Browser Groq is disabled — resume parsing uses parse-resume edge (Gemini on server).
 */
const buildProviders = (): LLMProvider[] => {
  appLogger.debug('[LangChain] Client-side Groq disabled — using parse-resume edge (Gemini on server)');
  return [];
};

const llmProviders: LLMProvider[] = buildProviders();

const getAvailableProvider = (): LLMProvider | null => {
  const now = Date.now();
  for (const p of llmProviders) {
    if (p.rateLimitedUntil > 0 && now < p.rateLimitedUntil) continue;
    if (p.rateLimitedUntil > 0 && now >= p.rateLimitedUntil) p.rateLimitedUntil = 0;
    return p;
  }
  return null;
};

const markProviderRateLimited = (provider: LLMProvider, err: unknown) => {
  const secs = extractRetryAfterSec(err) ?? 120;
  provider.rateLimitedUntil = Date.now() + secs * 1000;
  appLogger.warn(`[LangChain] ${provider.label} rate-limited for ${Math.round(secs)}s — trying next provider`);
};

/**
 * Run a callback against the provider chain.  Automatically cascades through
 * providers on 429 / network errors.  Returns null when all providers exhausted.
 */
const withProviderChain = async <T>(
  fn: (llm: ChatGroq, provider: LLMProvider) => Promise<T | null>,
): Promise<T | null> => {
  const now = Date.now();
  for (const p of llmProviders) {
    if (p.rateLimitedUntil > 0 && now < p.rateLimitedUntil) continue;
    if (p.rateLimitedUntil > 0) p.rateLimitedUntil = 0;
    try {
      await waitForRateLimit();
      const result = await fn(p.instance, p);
      return result;
    } catch (err) {
      if (is429(err)) {
        markProviderRateLimited(p, err);
        continue;
      }
      appLogger.warn(`[LangChain] ${p.label} failed (non-429):`, err);
      continue;
    }
  }
  appLogger.warn('[LangChain] All providers exhausted — falling back to regex');
  return null;
};

// Backward compat — single LLM reference used by class methods that haven't been
// migrated to withProviderChain yet.
const initializeLLM = (): ChatGroq | null => {
  const p = getAvailableProvider();
  return p ? p.instance : null;
};

// ============================================================================
// RESUME PARSING WITH LANGCHAIN
// ============================================================================

/**
 * Universal Resume Parser using LangChain
 * Supports: PDF, DOCX, TXT, Images (OCR), HTML, and more
 */
export class LangChainResumeParser {
  private llm: ChatGroq | null;
  private textSplitter: RecursiveCharacterTextSplitter;

  constructor() {
    this.llm = initializeLLM();
    this.textSplitter = new RecursiveCharacterTextSplitter({
      chunkSize: 2000,
      chunkOverlap: 200,
      separators: ['\n\n', '\n', '. ', ', ', ' ', '']
    });
  }

  /**
   * Main parsing function - detects format and routes to appropriate parser
   */
  async parseResume(file: File): Promise<ParsedResumeData> {
    const startTime = Date.now();
    appLogger.debug('[LangChain] Starting resume parsing for:', file.name, file.type);

    try {
      let rawText = '';
      let metadata = {
        format: file.type,
        processingTime: 0,
        pageCount: undefined as number | undefined,
        confidence: undefined as number | undefined
      };

      let documentSections: ParsedResumeSection[] | undefined;

      // Prefer document-native section extraction for PDF and DOCX (library-based structure)
      try {
        const { parseDocumentWithSections } = await import('../utils/documentSectionParser');
        const docResult = await parseDocumentWithSections(file);
        if (docResult && docResult.sections.length > 0) {
          rawText = docResult.fullText;
          documentSections = docResult.sections;
          if (file.type === 'application/pdf' && docResult.pageCount) metadata.pageCount = docResult.pageCount;
          appLogger.debug('[LangChain] Using document-native sections', { sectionCount: documentSections.length });
        }
      } catch (e) {
        appLogger.debug('[LangChain] documentSectionParser not used', e);
      }

      // Route to appropriate parser when no document-native result (or to get pageCount for PDF)
      if (!rawText && file.type === 'application/pdf') {
        const result = await this.parsePDF(file);
        rawText = result.text;
        metadata.pageCount = result.pageCount;
        metadata.confidence = 0.9;
      } else if (!rawText && (file.type.includes('wordprocessingml') || file.type === 'application/msword')) {
        rawText = await this.parseDocx(file);
        metadata.confidence = 0.9;
      } else if (rawText && file.type === 'application/pdf' && !metadata.pageCount) {
        const result = await this.parsePDF(file);
        metadata.pageCount = result.pageCount;
      }
      if (!rawText && file.type === 'text/plain') {
        rawText = await this.parseText(file);
        metadata.confidence = 1.0;
      } else if (!rawText && file.type.startsWith('image/')) {
        const result = await this.parseImage(file);
        rawText = result.text;
        metadata.confidence = result.confidence;
      } else if (!rawText && file.type === 'text/html') {
        rawText = await this.parseHTML(file);
        metadata.confidence = 0.8;
      } else if (!rawText) {
        // Fallback: try to parse as text
        appLogger.warn('[LangChain] Unknown file type, attempting text parsing');
        rawText = await this.parseText(file);
        metadata.confidence = 0.5;
      }
      if (documentSections && metadata.confidence === undefined) metadata.confidence = 0.95;

      if (!rawText || rawText.trim().length < 50) {
        throw new Error('Insufficient text extracted from document');
      }

      appLogger.debug('[LangChain] Raw text extracted, length:', rawText.length);

      // Structure the resume using AI
      const structured = await this.structureResumeWithAI(rawText);
      
      metadata.processingTime = Date.now() - startTime;

      appLogger.debug('[LangChain] Resume parsing completed successfully');
      
      return {
        structured,
        rawText,
        metadata,
        ...(documentSections && documentSections.length > 0 ? { sections: documentSections } : {}),
      };

    } catch (error) {
      appLogger.error('[LangChain] Resume parsing failed:', error);
      throw error;
    }
  }

  /**
   * PDF parsing using Browser-compatible pdf.js (pdfjs-dist)
   * Uses .mjs imports for proper Vite bundling
   */
  private async parsePDF(file: File): Promise<{ text: string; pageCount: number }> {
    try {
      appLogger.debug('[LangChain] Starting PDF parsing (browser-compatible pdf.js)');
      
      // Use .mjs imports for Vite compatibility (same as resumeParser.ts)
      const [pdfjsLib, pdfWorker] = await Promise.all([
        import('pdfjs-dist/build/pdf.mjs'),
        import('pdfjs-dist/build/pdf.worker.mjs?url')
      ]);
      
      // Set worker source
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker.default;

      // Load PDF from file
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdfDocument = await loadingTask.promise;

      const pageCount = pdfDocument.numPages;
      appLogger.debug('[LangChain] PDF loaded, pages:', pageCount);

      // Extract text from all pages
      const textPages: string[] = [];
      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        textPages.push(pageText);
      }

      const text = textPages.join('\n\n');

      if (!text || text.trim().length < 50) {
        throw new Error('Insufficient text extracted from PDF');
      }

      appLogger.debug('[LangChain] PDF parsed successfully with pdf.js', {
        textLength: text.length,
        pageCount,
        firstChars: text.substring(0, 100) + '...'
      });

      return { text, pageCount };
    } catch (error) {
      appLogger.error('[LangChain] PDF parsing failed:', error);
      throw new Error('Failed to parse PDF file. Please ensure it contains readable text (not scanned images).');
    }
  }

  /**
   * DOCX parsing using Browser-compatible method
   */
  private async parseDocx(file: File): Promise<string> {
    try {
      appLogger.debug('[LangChain] Starting DOCX parsing (browser-compatible method)');
      
      // BROWSER-COMPATIBLE METHOD: Use ArrayBuffer and manual parsing
      // LangChain's DocxLoader requires Node.js Buffer which isn't available in browsers
      const arrayBuffer = await file.arrayBuffer();
      
      // Try using docx lib if available, otherwise use basic text extraction
      try {
        // Dynamic import of mammoth for browser-compatible DOCX parsing
        const mammoth = await import('mammoth');
        const result = await mammoth.extractRawText({ arrayBuffer });
        const text = result.value;
        
        appLogger.debug('[LangChain] DOCX parsed successfully with mammoth.js', {
          textLength: text.length,
          firstChars: text.substring(0, 100) + '...'
        });
        
        return text;
      } catch (mammothError) {
        appLogger.warn('[LangChain] mammoth.js not available, trying basic extraction:', mammothError);
        
        // Fallback: Try to extract text using basic methods
        // Convert ArrayBuffer to text (this works for simple DOCX files)
        const decoder = new TextDecoder('utf-8');
        const rawText = decoder.decode(arrayBuffer);
        
        // Extract readable text (removing binary data)
        const cleanedText = rawText
          .replace(/[^\x20-\x7E\n\r\t]/g, ' ') // Remove non-printable chars
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();
        
        if (cleanedText.length < 50) {
          throw new Error('Insufficient text extracted from DOCX');
        }
        
        appLogger.debug('[LangChain] DOCX parsed with basic extraction', {
          textLength: cleanedText.length
        });
        
        return cleanedText;
      }
    } catch (error) {
      appLogger.error('[LangChain] DOCX parsing failed completely:', error);
      throw new Error('Failed to parse DOCX file. Please try converting to PDF or TXT format.');
    }
  }

  /**
   * Text file parsing
   */
  private async parseText(file: File): Promise<string> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const decoder = new TextDecoder('utf-8');
      return decoder.decode(arrayBuffer);
    } catch (error) {
      appLogger.error('[LangChain] Text parsing failed');
      throw error;
    }
  }

  /**
   * Image parsing using Tesseract.js OCR
   */
  private async parseImage(file: File): Promise<{ text: string; confidence: number }> {
    try {
      appLogger.debug('[LangChain] Starting OCR processing for image');
      
      const worker = await createWorker();
      await worker.loadLanguage('eng');
      await worker.initialize('eng');
      
      const { data } = await worker.recognize(file);
      await worker.terminate();

      const confidence = data.confidence / 100; // Convert to 0-1 scale
      
      appLogger.debug('[LangChain] OCR completed with confidence:', confidence);
      
      return {
        text: data.text,
        confidence
      };
    } catch (error) {
      appLogger.error('[LangChain] OCR parsing failed');
      throw error;
    }
  }

  /**
   * HTML parsing using Cheerio
   */
  private async parseHTML(file: File): Promise<string> {
    try {
      const text = await file.text();
      const $ = cheerio.load(text);
      
      // Remove script and style elements
      $('script, style, nav, footer, header').remove();
      
      // Extract text content
      return $('body').text().replace(/\s+/g, ' ').trim();
    } catch (error) {
      appLogger.error('[LangChain] HTML parsing failed');
      throw error;
    }
  }

  /**
   * Clean JSON response by removing markdown code blocks and extra whitespace
   */
  private cleanJsonResponse(text: string): string {
    // Remove markdown code blocks (```json ... ``` or ``` ... ```)
    let cleaned = text.replace(/```json\s*/gi, '').replace(/```\s*/g, '');
    
    // Remove any leading/trailing whitespace
    cleaned = cleaned.trim();
    
    // If there are multiple JSON objects, try to extract the first one
    const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      cleaned = jsonMatch[0];
    }
    
    return cleaned;
  }

  private readonly STRUCTURE_PROMPT = PromptTemplate.fromTemplate(`
You are a world-class ATS resume parser. Parse the resume in TWO STEPS.

STEP 1 — FIND ALL SECTION HEADERS.
Scan the ENTIRE text and identify every section header, including headers that appear INSIDE another section's block (e.g. "Professional Education and Certification" embedded at the bottom of experience text).
List them in your JSON under "_sections_found".

STEP 2 — EXTRACT AND MAP.
Using those identified headers as section boundaries, extract every line into the CORRECT canonical key below.

CANONICAL SECTIONS (use EXACTLY these keys):
"Contact"               — Name, email, phone, LinkedIn, location ONLY.
"Professional Summary"  — Profile/summary paragraphs ONLY. No skills, no jobs.
"Experience"            — ALL paid roles: "Company | Title | Dates" then bullets. STOP at any education heading.
"Education"             — ALL degrees, diplomas, universities, years. INCLUDES content under "Professional Education and Certification",
                          "Academic Background", "Qualifications", any "Pursuing/Completed [degree] from [school]" line.
"Skills"                — ALL technical skills, tools, frameworks, cloud, AI/ML, methodologies. Preserve sub-categories.
"Certifications"        — Professional credentials ONLY (SCJP, AWS, PMP, DMND). NOT university degrees.
"Awards & Honors"       — Awards, recognition, achievements.
"Technical Proficiencies" — Spoken/written human languages (English, Hindi, Spanish, etc.) and proficiency levels.
"Additional Information" — References, volunteer, publications, anything else.

CRITICAL RULES:
1. "Professional Education and Certification" embedded in experience text → all content after it goes to Education/Certifications.
2. "Language:" section with spoken languages → Technical Proficiencies. NOT Certifications.
3. Degrees from universities → Education. NEVER Experience or Certifications.
4. Skill sub-categories (Cloud Technologies, AI/ML, Languages & Frameworks) → Skills.
5. Each line appears in EXACTLY ONE section. No duplication.
6. Preserve exact original wording.

Resume text:
{resumeText}

Return ONLY valid JSON — no markdown, no explanation.
{{
  "_sections_found": ["header1", "header2", "..."],
  "Contact": [{{"text": "Full Name"}}, {{"text": "email"}}, {{"text": "phone"}}],
  "Professional Summary": [{{"text": "summary..."}}],
  "Experience": [{{"text": "Company | Title | Dates"}}, {{"text": "bullet"}}],
  "Education": [{{"text": "Degree, University (Year)"}}, {{"text": "Diploma, Institute (Year)"}}],
  "Skills": [{{"text": "Category: item1, item2"}}, {{"text": "standalone skill"}}],
  "Certifications": [{{"text": "Cert name (year)"}}],
  "Awards & Honors": [{{"text": "award"}}],
  "Technical Proficiencies": [{{"text": "English (Native), Hindi (Fluent)"}}],
  "Professional Affiliations": [],
  "Additional Information": [{{"text": "..."}}]
}}
  `);

  /**
   * Structure resume text using AI — cascades through the provider chain.
   */
  private async structureResumeWithAI(rawText: string): Promise<StructuredResume> {
    if (llmProviders.length === 0) {
      return this.fallbackStructuring(rawText);
    }

    const truncatedText = rawText.substring(0, 20000);
    const self = this;

    const aiResult = await withProviderChain(async (llm, provider) => {
      appLogger.debug(`[LangChain] structureResumeWithAI via ${provider.label}`);
      const chain = new LLMChain({ llm, prompt: self.STRUCTURE_PROMPT });
      const result = await chain.call({ resumeText: truncatedText });
      const cleanedJson = self.cleanJsonResponse(result.text);
      const structuredData = JSON.parse(cleanedJson);
      return self.sanitizeStructuredResume(structuredData);
    });

    if (aiResult) return aiResult;

    appLogger.warn('[LangChain] All providers failed for structureResumeWithAI — regex fallback');
    return this.fallbackStructuring(rawText);
  }

  /**
   * Public: structure resume text using CLIENT-SIDE LLM only.
   * Returns null if server LLM unavailable so the caller can fall through to regex fallback.
   * The regex fallback is intentionally NOT called here — it is Priority 3 in parseResume().
   */
  async structureResumeFromText(rawText: string): Promise<StructuredResume | null> {
    if (!rawText || rawText.trim().length < 100) return null;
    if (llmProviders.length === 0) return null;
    try {
      const structured = await this.structureResumeWithAI(rawText);
      const hasContent = Object.values(structured).some(
        (arr) => Array.isArray(arr) && arr.some((item: unknown) => {
          const t = typeof item === 'string' ? item : (item && typeof item === 'object' && 'text' in item ? (item as { text: string }).text : '');
          return typeof t === 'string' && t.trim().length > 0;
        })
      );
      return hasContent ? structured : null;
    } catch {
      return null;
    }
  }

  /**
   * Call the parse-resume edge function (server-side Gemini).
   * Returns sections array on success, null on failure so caller can fall back.
   */
  private async structureResumeViaEdgeFunction(
    rawText: string
  ): Promise<Array<{ title: string; content: string; preview: string }> | null> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) return null;

      const fnUrl = `${supabaseUrl}/functions/v1/parse-resume`;
      appLogger.debug('[LangChain] Calling parse-resume edge function');

      // Use Supabase anon key so no user session is required for upload flow
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
      const resp = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(anonKey ? { Authorization: `Bearer ${anonKey}` } : {}),
        },
        body: JSON.stringify({ resumeText: rawText }),
        signal: AbortSignal.timeout(45_000),
      });

      if (!resp.ok) {
        appLogger.warn('[LangChain] parse-resume edge function returned', resp.status);
        return null;
      }

      const data = await resp.json() as { sections?: Array<{ title: string; content: string; preview: string }> | null };
      if (!Array.isArray(data.sections) || data.sections.length === 0) {
        appLogger.warn('[LangChain] parse-resume returned no sections');
        return null;
      }

      appLogger.debug('[LangChain] parse-resume edge function →', data.sections.length, 'sections');
      return data.sections;
    } catch (e) {
      appLogger.warn('[LangChain] parse-resume edge function failed:', e);
      return null;
    }
  }

  /**
   * Section-boundary detection only: LLM identifies the resume's natural sections and returns
   * them with original titles and full content. No fixed schema, no rewriting, no bucketing.
   * Use this so content is not split or misplaced into wrong sections.
   */
  private readonly BOUNDARY_PROMPT = PromptTemplate.fromTemplate(`
You are a document structure analyzer. Your job is to split a resume into its natural sections.

Rules:
1. Identify section headings (even if formatting is inconsistent: ALL CAPS, bold, plain, with or without colons).
2. Group ALL content under the correct heading. Every line of the resume must appear in exactly one section.
3. Preserve original wording exactly. Do NOT summarize, rewrite, or omit any content.
4. If content appears before any heading, group it under "General" or "Contact" or the first section it clearly belongs to.
5. Strip only obvious noise: page numbers (e.g. "Page 1 of 2"), repeated identical headers on every page, and excessive blank lines. Keep all real content.
6. Return ONLY valid JSON. No markdown, no explanation.

Return format:
{{
  "sections": [
    {{ "title": "Section Title", "content": "Full original text belonging to this section" }},
    ...
  ]
}}

Resume text:
"""
{resumeText}
"""
`);

  async detectResumeSectionBoundaries(rawText: string): Promise<Array<{ title: string; content: string }> | null> {
    if (!rawText || rawText.trim().length < 50) return null;
    if (llmProviders.length === 0) return null;

    const truncatedText = rawText.substring(0, 20000);
    const self = this;

    return withProviderChain(async (llm, provider) => {
      appLogger.debug(`[LangChain] detectResumeSectionBoundaries via ${provider.label}`);
      const chain = new LLMChain({ llm, prompt: self.BOUNDARY_PROMPT });
      const result = await chain.call({ resumeText: truncatedText });
      const cleaned = self.cleanJsonResponse(result.text);
      const parsed = JSON.parse(cleaned) as { sections?: Array<{ title?: string; content?: string }> };
      const sections = parsed?.sections;
      if (!Array.isArray(sections) || sections.length === 0) return null;
      const out: Array<{ title: string; content: string }> = [];
      for (const s of sections) {
        const title = (s.title && String(s.title).trim()) || 'Section';
        const content = (s.content && String(s.content).trim()) || '';
        if (content.length > 0) out.push({ title, content });
      }
      return out.length > 0 ? out : null;
    });
  }

  /**
   * Sanitize and validate structured resume data
   */
  private sanitizeStructuredResume(data: any): StructuredResume {
    const empty: Array<{ text: string }> = [{ text: '' }];
    const arr = (v: unknown) => (Array.isArray(v) && v.length > 0 ? v : null);

    // Merge alternate key names the LLM might return
    const contact = arr(data.Contact) ?? arr(data.contact) ?? empty;
    const summary = arr(data['Professional Summary']) ?? arr(data['Summary']) ?? arr(data['Profile']) ?? arr(data['Objective']) ?? empty;
    const experience = arr(data.Experience) ?? arr(data['Work Experience']) ?? arr(data['Professional Experience']) ?? arr(data['Career History']) ?? arr(data['Employment']) ?? empty;
    const education =
      arr(data.Education) ??
      arr(data['Professional Education and Certification']) ??
      arr(data['Academic Background']) ??
      empty;
    const skills = arr(data.Skills) ?? arr(data['Technical Skills']) ?? arr(data['Core Competencies']) ?? empty;
    const certs = arr(data.Certifications) ?? arr(data.Certification) ?? arr(data['Licenses']) ?? empty;
    const awards = arr(data['Awards & Honors']) ?? arr(data.Awards) ?? arr(data.Honors) ?? arr(data.Achievements) ?? empty;
    // Spoken/written human languages → "Languages" bucket (maps to Languages & Frameworks in TITLE_TO_BUCKET)
    const techProf =
      arr(data['Languages']) ??
      arr(data['Language']) ??
      arr(data['Technical Proficiencies']) ??
      arr(data['Spoken Languages']) ??
      arr(data['Languages Known']) ??
      empty;
    const affiliations = arr(data['Professional Affiliations']) ?? empty;
    const additional = arr(data['Additional Information']) ?? arr(data['Additional']) ?? empty;

    return {
      'Contact': contact,
      'Professional Summary': summary,
      'Skills': skills,
      'Work Experience': experience,
      'Education': education,
      'Certifications': certs,
      'Awards & Honors': awards,
      'Languages': techProf,
      'Professional Affiliations': affiliations,
      'Additional Information': additional,
    };
  }

  /**
   * Fallback structuring when AI is not available or fails
   */
  private fallbackStructuring(rawText: string): StructuredResume {
    appLogger.debug('[LangChain] Using regex-based fallback parsing');
    
    const sections: Record<string, Array<{ text: string }>> = {
      'Contact': [],
      'Professional Summary': [],
      'Skills': [],
      'Experience': [],
      'Education': [],
      'Certifications': [],
      'Awards & Honors': [],
      'Technical Proficiencies': [],
      'Professional Affiliations': [],
      'Additional Information': []
    };

    const push = (key: string, text: string) => {
      const t = text.trim();
      if (!t) return;
      const arr = sections[key];
      if (arr) arr.push({ text: t });
    };

    // Headers at line start. Covers full variants like "Professional Education and Certification" and "Language:".
    const headerRegex = /(?:^|\n)\s*(Professional\s+Education(?:\s+(?:and|&)\s+Certifications?)?|Professional\s+Experience|Work\s+Experience|Career\s+History|Employment(?:\s+History)?|Work\s+History|Experience|Profile\s+Summary|Professional\s+Summary|Executive\s+Summary|Career\s+Summary|Summary\s+of\s+Qualifications|Summary|Profile|About(?:\s+Me)?|Objective|Career\s+Objective|Technical\s+Skills?|Key\s+Skills?|Core\s+Skills?|Skills?|Core\s+Competencies?|Competencies|Education(?:\s+(?:and|&)\s+(?:Certifications?|Training|Background))?|Academic\s+(?:Background|Qualifications?|Experience)|Qualifications?|Certifications?\s*(?:and\s+Licenses?)?|Licenses?\s*(?:and\s+Certifications?)?|Awards?\s*(?:(?:and|&)\s+Honors?)?|Honors?|Key\s+Achievements?|Achievements?|Languages?\s*(?:Known|Spoken)?|Spoken\s+Languages?|Professional\s+Affiliations?|Publications?|Projects?|Volunteer(?:ing)?|Interests?|Hobbies?|Additional\s+Information|References?)\s*[:\-]?\s*\n/gim;
    const headerToKey: Record<string, string> = {
      // Experience variants
      'professional experience': 'Experience',
      'work experience': 'Experience',
      'career history': 'Experience',
      'employment': 'Experience',
      'employment history': 'Experience',
      'work history': 'Experience',
      'experience': 'Experience',
      // Summary variants
      'profile summary': 'Professional Summary',
      'professional summary': 'Professional Summary',
      'executive summary': 'Professional Summary',
      'career summary': 'Professional Summary',
      'summary of qualifications': 'Professional Summary',
      'summary': 'Professional Summary',
      'profile': 'Professional Summary',
      'about': 'Professional Summary',
      'about me': 'Professional Summary',
      'objective': 'Professional Summary',
      'career objective': 'Professional Summary',
      // Skills variants
      'technical skills': 'Skills',
      'technical skill': 'Skills',
      'key skills': 'Skills',
      'core skills': 'Skills',
      'skills': 'Skills',
      'skill': 'Skills',
      'core competencies': 'Skills',
      'competencies': 'Skills',
      // Education variants — including "Professional Education and Certification"
      'professional education and certifications': 'Education',
      'professional education and certification': 'Education',
      'professional education': 'Education',
      'education and certifications': 'Education',
      'education and certification': 'Education',
      'education and training': 'Education',
      'education and background': 'Education',
      'education': 'Education',
      'academic background': 'Education',
      'academic qualifications': 'Education',
      'academic experience': 'Education',
      'qualifications': 'Education',
      // Certifications variants
      'certifications': 'Certifications',
      'certification': 'Certifications',
      'certifications and licenses': 'Certifications',
      'licenses and certifications': 'Certifications',
      'licenses': 'Certifications',
      // Awards variants
      'awards': 'Awards & Honors',
      'honors': 'Awards & Honors',
      'awards and honors': 'Awards & Honors',
      'awards & honors': 'Awards & Honors',
      'achievements': 'Awards & Honors',
      'key achievements': 'Awards & Honors',
      // Languages — both singular and plural → Technical Proficiencies
      'languages': 'Technical Proficiencies',
      'language': 'Technical Proficiencies',
      'languages known': 'Technical Proficiencies',
      'languages spoken': 'Technical Proficiencies',
      'spoken languages': 'Technical Proficiencies',
      // Additional / misc
      'professional affiliations': 'Additional Information',
      'publications': 'Additional Information',
      'projects': 'Additional Information',
      'volunteer': 'Additional Information',
      'volunteering': 'Additional Information',
      'interests': 'Additional Information',
      'hobbies': 'Additional Information',
      'additional information': 'Additional Information',
      'references': 'Additional Information',
      'reference': 'Additional Information',
    };

    type Match = { headerStart: number; contentStart: number; key: string };
    const matches: Match[] = [];
    let m: RegExpExecArray | null;
    headerRegex.lastIndex = 0;
    while ((m = headerRegex.exec(rawText)) !== null) {
      const headerName = (m[1] || '').trim().toLowerCase();
      const key = headerToKey[headerName] || 'Additional Information';
      matches.push({ headerStart: m.index, contentStart: m.index + m[0].length, key });
    }

    // Top block (before first section header) → Contact (name, email, phone, LinkedIn) + Professional Summary
    const topEnd = matches.length ? matches[0].headerStart : rawText.length;
    const top = rawText.slice(0, topEnd).trim();
    if (top) {
      const topLines = top.split(/\n/).map((l) => l.trim()).filter(Boolean);
      const looksLikeName = (line: string) => line.length >= 3 && line.length <= 50 && !/@|http|\.com|linkedin|\d{5}/i.test(line) && line.split(/\s+/).length >= 1 && line.split(/\s+/).length <= 4;
      const contactLines: string[] = [];
      const summaryParts: string[] = [];
      for (const line of topLines) {
        if (looksLikeName(line) && summaryParts.length === 0 && contactLines.length === 0) {
          contactLines.push(line);
          continue;
        }
        if (/^[\w.-]+@[\w.-]+\.\w+/.test(line)) {
          contactLines.push(line.includes('Email:') ? line : `Email: ${line.match(/[\w.-]+@[\w.-]+\.\w+/)?.[0] || line}`);
          continue;
        }
        if (/^(\+?\d[\d\s\-().]{8,}|phone|tel|mobile)\s*:?\s*/i.test(line) || (line.length < 80 && /linkedin\.com|linkedin\s*:/i.test(line))) {
          contactLines.push(line);
          continue;
        }
        if (line.includes('|') && (line.includes('@') || line.includes('+') || /linkedin/i.test(line))) {
          contactLines.push(line);
          continue;
        }
        if (line.length > 20) summaryParts.push(line);
      }
      contactLines.forEach((c) => sections['Contact'].push({ text: c }));
      if (summaryParts.length) push('Professional Summary', summaryParts.join('\n'));
    }

    // Between headers: assign full content to the section (no truncation)
    for (let i = 0; i < matches.length; i++) {
      const start = matches[i].contentStart;
      const end = i + 1 < matches.length ? matches[i + 1].headerStart : rawText.length;
      const content = rawText.slice(start, end).trim();
      if (content) push(matches[i].key, content);
    }

    Object.keys(sections).forEach(key => {
      if (sections[key].length === 0) sections[key].push({ text: '' });
    });

    return sections as StructuredResume;
  }
}

// ============================================================================
// JOB URL SCRAPING WITH LANGCHAIN
// ============================================================================

/**
 * Universal Job Scraper using LangChain
 * Supports all major job portals with AI-powered extraction
 */
export class LangChainJobScraper {
  private llm: ChatGroq | null;

  constructor() {
    this.llm = initializeLLM();
  }

  /**
   * Main scraping function - works with any job portal
   */
  async scrapeJobUrl(url: string): Promise<ParsedJobData> {
    const startTime = Date.now();
    appLogger.debug('[LangChain] Starting job scraping for:', url);

    try {
      // Try to load the webpage directly with LangChain
      // This will work for most job portals, even if they require authentication
      // The CheerioWebBaseLoader can often bypass basic protection
      const loader = new CheerioWebBaseLoader(url, {
        selector: 'body' // Load entire body content
      });

      const docs = await loader.load();
      
      if (docs.length === 0) {
        throw new Error('No content loaded from URL');
      }

      const pageContent = docs[0].pageContent;
      appLogger.debug('[LangChain] Page content loaded, length:', pageContent.length);

      // Extract job data using AI
      const jobData = await this.extractJobDataWithAI(pageContent, url);
      
      jobData.metadata.processingTime = Date.now() - startTime;

      appLogger.debug('[LangChain] Job scraping completed successfully');
      
      return jobData;

    } catch (error) {
      appLogger.error('[LangChain] Job scraping failed:', error);
      
      // Provide helpful error message
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorString = JSON.stringify(error);
      
      // Check if it's a CORS, network, or authentication error
      // CORS errors often show as "Failed to fetch" or contain "CORS" in the message
      if (errorMessage.includes('CORS') || 
          errorMessage.includes('cors') ||
          errorMessage.includes('Access-Control') ||
          errorMessage.includes('401') || 
          errorMessage.includes('403') || 
          errorMessage.includes('Authentication') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('ERR_FAILED') ||
          errorString.includes('CORS') ||
          errorString.includes('cors')) {
        appLogger.warn('[LangChain] CORS/Network error detected, will trigger Supabase fallback');
        throw new Error('CORS_ERROR: Portal blocks direct access. Supabase fallback will be used.');
      }
      
      throw error;
    }
  }

  /**
   * Extract job data using AI — cascades through provider chain.
   */
  private readonly JOB_PROMPT = PromptTemplate.fromTemplate(`
    Extract job info from this content. Return JSON only:
    
    {content}

    Format:
    {{
      "jobTitle": "Software Engineer",
      "company": "Tech Corp",
      "location": "San Francisco, CA",
      "description": "Job description...",
      "requirements": ["Bachelor's degree", "3+ years experience"],
      "skills": ["JavaScript", "React", "Node.js"],
      "salary": "$80,000 - $120,000",
      "jobType": "Full-time",
      "experienceLevel": "Mid-level",
      "benefits": ["Health insurance", "401k"]
    }}
  `);

  private async extractJobDataWithAI(pageContent: string, url: string): Promise<ParsedJobData> {
    if (llmProviders.length === 0) {
      return this.fallbackJobExtraction(pageContent, url);
    }

    const truncatedContent = pageContent.substring(0, 4000);
    const self = this;
    const jobId = this.generateJobId(url);
    const portal = this.identifyPortal(url);

    const aiResult = await withProviderChain(async (llm, provider) => {
      appLogger.debug(`[LangChain] extractJobDataWithAI via ${provider.label}`);
      const chain = new LLMChain({ llm, prompt: self.JOB_PROMPT });
      const result = await chain.call({ content: truncatedContent });
      return JSON.parse(result.text);
    });

    if (aiResult) {
      return {
        jobId,
        title: aiResult.jobTitle || 'Unknown Title',
        company: aiResult.company || 'Unknown Company',
        location: aiResult.location || 'Unknown Location',
        description: aiResult.description || 'No description available',
        requirements: Array.isArray(aiResult.requirements) ? aiResult.requirements : [],
        skills: Array.isArray(aiResult.skills) ? aiResult.skills : [],
        salary: aiResult.salary,
        jobType: aiResult.jobType,
        experienceLevel: aiResult.experienceLevel,
        benefits: Array.isArray(aiResult.benefits) ? aiResult.benefits : [],
        metadata: {
          portal,
          scrapedAt: new Date(),
          confidence: 0.75,
          processingTime: 0,
        },
      };
    }

    return this.fallbackJobExtraction(pageContent, url);
  }

  /**
   * Fallback job extraction using regex when AI is not available
   */
  private fallbackJobExtraction(pageContent: string, url: string): ParsedJobData {
    appLogger.debug('[LangChain] Performing regex-based job extraction');
    
    const jobId = this.generateJobId(url);
    const portal = this.identifyPortal(url);
    
    // Extract job title (common patterns)
    let title = 'Job Title';
    const titlePatterns = [
      /<title>([^<|]+)/i,
      /job[- ]title[:\s]+([^\n]+)/i,
      /<h1[^>]*>([^<]+)<\/h1>/i
    ];
    for (const pattern of titlePatterns) {
      const match = pageContent.match(pattern);
      if (match && match[1].trim().length > 3) {
        title = match[1].trim().split(/[|\-–—]/)[0].trim();
        break;
      }
    }

    // Extract company name
    let company = 'Company';
    const companyPatterns = [
      /company[:\s]+([^\n]+)/i,
      /employer[:\s]+([^\n]+)/i,
      /<meta[^>]+property="og:site_name"[^>]+content="([^"]+)"/i
    ];
    for (const pattern of companyPatterns) {
      const match = pageContent.match(pattern);
      if (match && match[1].trim().length > 0) {
        company = match[1].trim();
        break;
      }
    }

    // Extract location
    let location = 'Location';
    const locationPatterns = [
      /location[:\s]+([^\n]+)/i,
      /city[:\s]+([^\n]+)/i,
      /<meta[^>]+name="geo\.region"[^>]+content="([^"]+)"/i
    ];
    for (const pattern of locationPatterns) {
      const match = pageContent.match(pattern);
      if (match && match[1].trim().length > 0) {
        location = match[1].trim();
        break;
      }
    }

    // Extract description (first significant paragraph)
    const descMatch = pageContent.match(/<p[^>]*>([^<]{100,1000})<\/p>/);
    const description = descMatch ? descMatch[1].trim() : 'No description available';

    // Extract skills (common technical terms)
    const skillsPattern = /\b(javascript|typescript|python|java|react|node\.js|angular|vue|sql|aws|docker|kubernetes|git|html|css|c\+\+|c#|php|ruby|swift|kotlin|go|rust)\b/gi;
    const skillMatches = pageContent.match(skillsPattern) || [];
    const skills = [...new Set(skillMatches.map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()))].slice(0, 10);

    return {
      jobId,
      title,
      company,
      location,
      description,
      requirements: [],
      skills,
      salary: undefined,
      jobType: undefined,
      experienceLevel: undefined,
      benefits: [],
      metadata: {
        portal,
        scrapedAt: new Date(),
        confidence: 0.5, // Lower confidence for fallback
        processingTime: 0
      }
    };
  }

  /**
   * Generate unique job ID from URL
   */
  private generateJobId(url: string): string {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const queryParams = urlObj.searchParams;
    
    // Try to extract ID from common patterns
    const jobIdPatterns = [
      /\/jobs\/view\/(\d+)/,  // LinkedIn
      /viewjob\?jk=([a-zA-Z0-9]+)/, // Indeed
      /\/job-listing\/([^/?]+)/, // Glassdoor
      /\/job\/detail\/([^/?]+)/, // Internshala
    ];

    for (const pattern of jobIdPatterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    // Fallback: generate from URL hash
    return btoa(url).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
  }

  /**
   * Identify job portal from URL
   */
  private identifyPortal(url: string): string {
    const domain = new URL(url).hostname.toLowerCase();
    
    // Return lowercase for consistent matching in protected portal checks
    if (domain.includes('linkedin.com')) return 'linkedin';
    if (domain.includes('indeed.com')) return 'indeed';
    if (domain.includes('glassdoor.com')) return 'glassdoor';
    if (domain.includes('naukri.com')) return 'naukri';
    if (domain.includes('monster.com')) return 'monster';
    if (domain.includes('shine.com')) return 'shine';
    if (domain.includes('timesjobs.com')) return 'timesjobs';
    if (domain.includes('internshala.com')) return 'internshala';
    if (domain.includes('bayt.com')) return 'bayt';
    if (domain.includes('foundit.in')) return 'foundit';
    if (domain.includes('instahyre.com')) return 'instahyre';
    if (domain.includes('apna.co')) return 'apna';
    
    return 'other';
  }

  /**
   * Parse job description text using LLM (no scraping needed!)
   * This bypasses authentication issues with protected portals
   */
  async parseJobDescriptionText(jobText: string, jobUrl?: string): Promise<ParsedJobData> {
    appLogger.debug('[LangChain] Parsing job description text with LLM');
    
    try {
      const jobData = await this.extractJobDataWithAI(jobText, jobUrl || 'manual-paste');
      appLogger.debug('[LangChain] Job description parsed successfully from text');
      return jobData;
    } catch (error) {
      appLogger.error('[LangChain] LLM parsing failed, using fallback:', error);
      return this.fallbackJobExtraction(jobText, jobUrl || 'manual-paste');
    }
  }
}

// ============================================================================
// DATABASE PERSISTENCE SERVICE
// ============================================================================

/**
 * Database service for persisting parsed data
 */
export class LangChainDatabaseService {
  
  /**
   * Save parsed resume to database
   */
  async saveResumeData(userId: string, fileName: string, parsedData: ParsedResumeData): Promise<string> {
    try {
      appLogger.debug('[LangChain] Saving resume data to database');

      const { data, error } = await supabase
        .from('user_resumes')
        .insert({
          user_id: userId,
          original_file: fileName,
          parsed_json: {
            structured: parsedData.structured,
            metadata: parsedData.metadata,
            rawText: parsedData.rawText, // ✅ COMPLETE resume text - no truncation
            langchainParsed: true
          },
          format: parsedData.metadata.format,
          upload_timestamp: new Date().toISOString()
        })
        .select('resume_id')
        .single();

      if (error) {
        throw new Error(`Database save failed: ${error.message}`);
      }

      appLogger.debug('[LangChain] Resume data saved successfully');
      return data.resume_id;
    } catch (error) {
      appLogger.error('[LangChain] Database save failed:', error);
      throw error;
    }
  }

  /**
   * Save parsed job data to database
   */
  async saveJobData(parsedData: ParsedJobData): Promise<string> {
    try {
      appLogger.debug('[LangChain] Saving job data to database');

      const { data, error } = await supabase
        .from('job_details')
        .upsert({
          job_url: parsedData.jobId, // Using jobId as unique identifier
          scraped_json: {
            ...parsedData,
            langchainParsed: true
          },
          job_title: parsedData.title,
          company_name: parsedData.company,
          location: parsedData.location,
          scraped_at: new Date().toISOString()
        })
        .select('job_id')
        .single();

      if (error) {
        throw new Error(`Database save failed: ${error.message}`);
      }

      appLogger.debug('[LangChain] Job data saved successfully');
      return data.job_id;
    } catch (error) {
      appLogger.error('[LangChain] Database save failed:', error);
      throw error;
    }
  }
}

// ============================================================================
// MAIN SERVICE EXPORTS
// ============================================================================

// Create the main parser instance
const mainParser = new LangChainResumeParser();

/** Format experience entry for structured/sections (used by compatibility wrapper). */
function formatExperienceEntry(entry: { company?: string; position?: string; duration?: string; location?: string; highlights?: string[] }): string {
  const parts: string[] = [];
  if (entry.position || entry.company) parts.push([entry.position, entry.company].filter(Boolean).join(' at '));
  if (entry.duration) parts.push(`(${entry.duration})`);
  if (entry.location) parts.push(entry.location);
  const header = parts.join(' ');
  const bullets = (entry.highlights || []).filter(Boolean).map((h) => (h.startsWith('•') ? h : `• ${h}`));
  return [header, ...bullets].filter(Boolean).join('\n');
}

/**
 * Build structured record from layout-derived sections (for DB and compatibility).
 */
function structuredFromSections(sections: Array<{ title: string; content: string }>): Record<string, unknown[]> {
  const structured: Record<string, unknown[]> = {};
  for (const s of sections) {
    const key = s.title.trim() || 'Content';
    const items = s.content.split(/\r?\n/).filter((l) => l.trim()).map((line) => ({ text: line }));
    if (items.length) structured[key] = items;
  }
  return structured;
}

// Text-only parsing: extract text → preprocess → organize from text (no layout/sections/boxes)
export const langchainResumeParser = {
  /**
   * Parse resume: extract plain text only, then preprocess and organize sections from text.
   * No document structure (lines, boxes, headings, font size). Text only.
   */
  async parseResume(file: File): Promise<{
    rawText: string;
    metadata: { format: string; confidence: number; processingTime: number; pageCount?: number; sectionsFound?: number };
    structured: Record<string, unknown[]>;
    sections: Array<{ title: string; content: string; preview: string }>;
  }> {
    const startTime = Date.now();
    const isPdf = file.type === 'application/pdf';
    const isDocx =
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword';

    try {
      if (isPdf || isDocx) {
        const { extractResumeText } = await import('../utils/extractResumeText');
        const { preprocessResumeText } = await import('../utils/pdfTextSanitizer');
        const { structuredResumeToSections, arrangeResumeSections, bucketResumeSections, sortSectionsByCanonicalOrder } = await import(
          '../modules/dashboard/components/AIResumeOptimizer/utils/parseResumeSections'
        );
        const rawExtracted = await extractResumeText(file);
        if (rawExtracted && rawExtracted.trim().length > 0) {
          console.group('[Resume Parse] PARSED RESUME (raw extracted text)');
          console.log('Length:', rawExtracted.length, 'chars');
          console.log('Preview (first 800 chars):', rawExtracted.slice(0, 800));
          console.groupEnd();

          const rawText = preprocessResumeText(rawExtracted);
          console.group('[Resume Parse] AFTER PREPROCESS (normalized) – full text');
          console.log('Length:', rawText.length, 'chars');
          console.log('Full normalized text:', rawText);
          console.groupEnd();

          // ── Priority 1: Server-side LLM via parse-resume edge (Gemini) ───────
          const edgeSections = await mainParser['structureResumeViaEdgeFunction'](rawText);
          if (edgeSections && edgeSections.length >= 3) {
            console.log('[Resume Parse] Edge function →', edgeSections.length, 'sections');
            const bucketed = bucketResumeSections(edgeSections);
            const sectionsList = bucketed
              .filter((s) => s.content.trim().length > 0)
              .map((s) => ({ title: s.title, content: s.content, preview: s.preview }));
            appLogger.debug(`[LangChain] Edge-function parse → buckets for: ${file.name}`, { sectionsFound: sectionsList.length });
            const sortedForDisplay = sortSectionsByCanonicalOrder(sectionsList);
            const structured = structuredFromSections(sortedForDisplay);
            return {
              rawText,
              metadata: {
                format: file.type,
                confidence: 0.92,
                processingTime: Date.now() - startTime,
                sectionsFound: sectionsList.length,
              },
              structured,
              sections: sectionsList,
            };
          }

          // ── Priority 2: Client-side LLM fallback (disabled unless keys added) ─
          const aiStructured = await mainParser.structureResumeFromText(rawText);
          if (aiStructured) {
            const sectionsFromAi = structuredResumeToSections(aiStructured as Record<string, unknown[]>);
            const hasOneHugeSection = sectionsFromAi.some((s) => (s.content.split(/\n/).filter((l) => l.trim()).length > 50));
            const tooFewSections = sectionsFromAi.length <= 2;
            if (sectionsFromAi.length >= 1 && !(tooFewSections && hasOneHugeSection)) {
              console.log('[Resume Parse] Client LLM →', sectionsFromAi.length, 'sections');
              const bucketed = bucketResumeSections(sectionsFromAi);
              const sectionsList = bucketed
                .filter((s) => s.content.trim().length > 0)
                .map((s) => ({ title: s.title, content: s.content, preview: s.preview }));
              appLogger.debug(`[LangChain] Client-LLM parse → buckets for: ${file.name}`, { sectionsFound: sectionsList.length });
              const structured: Record<string, unknown[]> = {};
              for (const [k, v] of Object.entries(aiStructured)) {
                if (Array.isArray(v)) structured[k] = v;
              }
              return {
                rawText,
                metadata: {
                  format: file.type,
                  confidence: 0.95,
                  processingTime: Date.now() - startTime,
                  sectionsFound: sectionsList.length,
                },
                structured,
                sections: sectionsList,
              };
            }
            if (tooFewSections && hasOneHugeSection) {
              console.log('[Resume Parse] Client LLM returned only', sectionsFromAi.length, 'sections with one very large – trying regex fallback');
            }
          }

          // ── Priority 3: Regex heuristic (last resort) ─────────────────────
          const { parseResumeSections } = await import(
            '../modules/dashboard/components/AIResumeOptimizer/utils/parseResumeSections'
          );
          const sectionsFromText = parseResumeSections(rawText);
          console.group('[Resume Parse] LAST-RESORT regex sections');
          console.log('Sections found:', sectionsFromText.length);
          console.groupEnd();

          const arranged = arrangeResumeSections(sectionsFromText);
          const bucketed = bucketResumeSections(arranged);
          const sectionsList = bucketed
            .filter((s) => s.content.trim().length > 0)
            .map((s) => ({ title: s.title, content: s.content, preview: s.preview }));
          appLogger.debug(`[LangChain] Regex parse for: ${file.name}`, { sectionsFound: sectionsList.length });
          const structured = structuredFromSections(sectionsList);
          return {
            rawText,
            metadata: {
              format: file.type,
              confidence: 0.75,
              processingTime: Date.now() - startTime,
              sectionsFound: sectionsList.length,
            },
            structured,
            sections: sectionsList,
          };
        }
      }

      appLogger.debug(`[LangChain] Fallback to resumeParser for: ${file.name}`);
      const { parseResume } = await import('../utils/resumeParser');
      const { preprocessResumeText } = await import('../utils/pdfTextSanitizer');
      const structuredResume = await parseResume(file);

      const structured: Record<string, unknown[]> = {};
      const sectionsList: Array<{ title: string; content: string; preview: string }> = [];
      let rawText = '';

      for (const [sectionName, sectionData] of Object.entries(structuredResume)) {
        if (!Array.isArray(sectionData)) continue;
        const textItems: string[] = [];
        const structuredItems: unknown[] = [];

        for (const item of sectionData) {
          if (typeof item === 'object' && item !== null && 'text' in item && typeof (item as { text: string }).text === 'string') {
            const t = (item as { text: string }).text;
            textItems.push(t);
            rawText += t + '\n';
            structuredItems.push({ text: t });
          } else if (typeof item === 'object' && item !== null && ('company' in item || 'position' in item || 'highlights' in item)) {
            const formatted = formatExperienceEntry(item as { company?: string; position?: string; duration?: string; highlights?: string[] });
            textItems.push(formatted);
            rawText += formatted + '\n';
            structuredItems.push({ text: formatted });
          } else if (typeof item === 'string') {
            textItems.push(item);
            rawText += item + '\n';
            structuredItems.push({ text: item });
          }
        }

        if (textItems.length > 0) {
          const content = textItems.join('\n').trim();
          structured[sectionName] = structuredItems;
          sectionsList.push({
            title: sectionName,
            content,
            preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : ''),
          });
        }
      }

      rawText = preprocessResumeText(rawText.trim());

      return {
        rawText,
        metadata: {
          format: file.type,
          confidence: 0.95,
          processingTime: Date.now() - startTime,
          sectionsFound: sectionsList.length,
        },
        structured,
        sections: sectionsList,
      };
    } catch (error) {
      appLogger.error('[LangChain] Resume parsing failed:', error);
      throw error;
    }
  }
};

/**
 * Preprocess resume with LLM for the Review step (two-pane). Prefer section-boundary detection
 * so the user's sections and content are preserved; fall back to schema-based structuring if needed.
 */
export async function preprocessResumeForReview(rawText: string): Promise<Array<{ title: string; content: string; preview: string }> | null> {
  if (!rawText || rawText.trim().length < 100) return null;
  try {
    const { preprocessResumeText } = await import('../utils/pdfTextSanitizer');
    const normalized = preprocessResumeText(rawText);

    // 1) Prefer section-boundary detection: preserve user's section names and exact content (no bucketing).
    const boundaries = await mainParser.detectResumeSectionBoundaries(normalized);
    if (boundaries && boundaries.length > 0) {
      appLogger.debug('[LangChain] preprocessResumeForReview: using section-boundary detection', { count: boundaries.length });
      const sections = boundaries.map((s) => ({
        title: s.title,
        content: s.content,
        preview: s.content.slice(0, 120).replace(/\n/g, ' ') + (s.content.length > 120 ? '…' : ''),
      }));
      const { sortSectionsByCanonicalOrder } = await import(
        '../modules/dashboard/components/AIResumeOptimizer/utils/parseResumeSections'
      );
      return sortSectionsByCanonicalOrder(sections);
    }

    // 2) Fallback: fixed-schema structuring then bucketing (may reassign content to canonical sections).
    const structured = await mainParser.structureResumeFromText(normalized);
    if (!structured) return null;
    const { structuredResumeToSections, bucketResumeSections } = await import(
      '../modules/dashboard/components/AIResumeOptimizer/utils/parseResumeSections'
    );
    const fromAi = structuredResumeToSections(structured as Record<string, unknown[]>);
    if (fromAi.length < 1) return null;
    const bucketed = bucketResumeSections(fromAi);
    return bucketed
      .filter((s) => s.content.trim().length > 0)
      .map((s) => ({
        title: s.title,
        content: s.content,
        preview: s.preview ?? s.content.slice(0, 120).replace(/\n/g, ' ') + (s.content.length > 120 ? '…' : ''),
      }));
  } catch (e) {
    appLogger.warn('[LangChain] preprocessResumeForReview failed', e);
    return null;
  }
}

export const langchainJobScraper = new LangChainJobScraper();
export const langchainDatabaseService = new LangChainDatabaseService();
